import React, { useState } from 'react';
import { ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface AudioPageLayoutProps {
  title: string;
  formSection: React.ReactNode;
  historySection: React.ReactNode;
  /**
   * 'inline' (default) renders the classic split layout with the history
   * panel always visible next to the form.
   * 'drawer' renders the form section full-width and puts the history
   * panel behind a toggleable slide-over drawer.
   */
  historyMode?: 'inline' | 'drawer';
}

const AudioPageLayout: React.FC<AudioPageLayoutProps> = ({
  title,
  formSection,
  historySection,
  historyMode = 'inline',
}) => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  if (historyMode === 'drawer') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Page Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="px-3 py-2 flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h1>
            <button
              type="button"
              onClick={() => setIsHistoryOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <ClockIcon className="h-4 w-4" />
              History
            </button>
          </div>
        </div>

        {/* Main Content - Full Width */}
        <div className="px-3 py-3">
          <div className="overflow-y-auto h-[calc(100vh-100px)]">
            {formSection}
          </div>
        </div>

        {/* History Drawer */}
        {isHistoryOpen && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setIsHistoryOpen(false)}
            />
            <div className="relative w-full max-w-md h-full bg-white dark:bg-gray-800 shadow-xl flex flex-col transition-transform duration-300 ease-out translate-x-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">History</h2>
                <button
                  type="button"
                  onClick={() => setIsHistoryOpen(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                {historySection}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h1>
        </div>
      </div>

      {/* Main Content - Split Layout */}
      <div className="px-3 py-3">
        <div className="grid grid-cols-1 lg:grid-cols-[5fr_4fr] gap-4 h-[calc(100vh-100px)]">
          {/* Left Side - Form Section */}
          <div className="overflow-y-auto">
            {formSection}
          </div>

          {/* Right Side - History Section */}
          <div className="overflow-hidden">
            {historySection}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPageLayout;
